/**
 * "Traé tu histórico": engancha partidos que YA están en el archivo a una fase
 * del fixture.
 *
 * Es el camino de migración desde el `competition` de texto libre que traían
 * los `match_created`: la mesa filtra por ese texto, marca los que son y los
 * sube a la fecha. El resultado no se carga acá —lo calcula el API leyendo el
 * log— así que enganchar 30 partidos viejos llena la tabla de posiciones sola.
 *
 * Sólo se muestra a quien tiene `competitions:manage`; el API igual lo exige.
 */
import { useState } from 'react';
import type { Competition } from '@scout/core';
import { useT } from '../../i18n/use-t';
import { Card, Empty, Loading } from '../../components/States';
import { errorMessage, useAsync } from '../../hooks';
import type { ApiClient } from '../../lib/api-client';

export function LinkMatchesCard({
  client,
  competition,
  onLinked,
}: {
  client: ApiClient;
  competition: Competition;
  onLinked: () => void;
}): JSX.Element | null {
  const t = useT();
  const [filter, setFilter] = useState('');
  const [applied, setApplied] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [phaseId, setPhaseId] = useState(competition.phases[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const candidates = useAsync(
    () => client.getFixtureCandidates(competition.id, applied === '' ? undefined : applied),
    [client, competition.id, applied],
  );
  const matches = candidates.data?.matches ?? [];

  if (competition.phases.length === 0) return null;

  const toggle = (matchId: string): void => {
    setSelected((prev) =>
      prev.includes(matchId) ? prev.filter((id) => id !== matchId) : [...prev, matchId],
    );
  };

  const link = async (): Promise<void> => {
    if (selected.length === 0 || phaseId === '') return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await client.importMatches(competition.id, {
        phaseId,
        matchIds: selected,
      });
      setNotice(
        t('competition.linkDone', {
          linked: result.linked,
          teams: result.addedTeams.length,
        }) +
          (result.skipped.length > 0
            ? ` ${t('competition.linkSkipped', { count: result.skipped.length })}`
            : ''),
      );
      setSelected([]);
      candidates.reload();
      onLinked();
    } catch (err) {
      setNotice(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t('competition.linkTitle')}>
      <p className="hint">{t('competition.linkHint')}</p>

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <label className="field" style={{ flex: '1 1 240px' }}>
          <span>{t('competition.linkFilter')}</span>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setApplied(filter);
            }}
          />
        </label>
        <label className="field">
          <span>{t('competition.phase')}</span>
          <select value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
            {competition.phases.map((phase) => (
              <option key={phase.id} value={phase.id}>
                {phase.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="small" onClick={() => setApplied(filter)}>
          {t('common.refresh')}
        </button>
      </div>

      {notice !== null ? <p className="hint">{notice}</p> : null}

      {candidates.status === 'loading' && candidates.first ? (
        <Loading what={t('matches.loadingWhat')} />
      ) : null}
      {candidates.status === 'ok' && matches.length === 0 ? (
        <Empty>{t('competition.linkEmpty')}</Empty>
      ) : null}

      {matches.length > 0 ? (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th />
                  <th>{t('matches.colMatch')}</th>
                  <th>{t('competition.colName')}</th>
                  <th>{t('matches.colUpdated')}</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((match) => (
                  <tr key={match.matchId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.includes(match.matchId)}
                        onChange={() => toggle(match.matchId)}
                        aria-label={match.name}
                      />
                    </td>
                    <td>
                      <strong>{match.name}</strong>
                      <div className="hint">{match.matchId}</div>
                    </td>
                    {/* El string libre que traía el partido: es lo que se está
                        reemplazando por la entidad competencia. */}
                    <td>{match.competition ?? <span className="hint">·</span>}</td>
                    <td>{match.updatedAt.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="primary"
              disabled={busy || selected.length === 0}
              onClick={() => void link()}
            >
              {t('competition.linkAction', { count: selected.length })}
            </button>
            <button
              type="button"
              className="small"
              onClick={() => setSelected(matches.map((m) => m.matchId))}
            >
              {t('competition.selectAll')}
            </button>
            <button type="button" className="small" onClick={() => setSelected([])}>
              {t('competition.clearSelection')}
            </button>
          </div>
        </>
      ) : null}
    </Card>
  );
}
